import * as React from "react";
import { CalendarIcon, Clock } from "lucide-react";
import { calendarDateISO, displayDate, formatDateEntry, formatTimeEntry, validTime } from "@workspace/date-time";
import { cn } from "@/lib/utils";

type Kind = "date" | "time" | "datetime-local";
type Props = React.ComponentProps<"input"> & { type: Kind; inputClassName: string; onValidityChange?: (valid: boolean) => void };

function formatValue(type: Kind, raw: string): string {
  if (type === "date") return formatDateEntry(raw);
  if (type === "time") return formatTimeEntry(raw);
  if (!raw) return "";
  const [date, ...time] = raw.split(/[T ]/);
  if (time.length) return `${formatDateEntry(date)} ${formatTimeEntry(time.join(""))}`;
  const digits = raw.replace(/\D/g, "");
  if (!raw.includes("/") && digits.length > 8) return `${formatDateEntry(digits.slice(0, 8))} ${formatTimeEntry(digits.slice(8))}`;
  return formatDateEntry(raw);
}
function storedValue(type: Kind, draft: string): string {
  if (type === "time") return draft;
  if (type === "date") return calendarDateISO(draft) ?? draft;
  const [date, time] = draft.split(" ");
  const iso = calendarDateISO(date);
  return iso && validTime(time ?? "") ? `${iso}T${time}` : draft;
}
function isValid(type: Kind, value: string): boolean {
  if (!value) return true;
  if (type === "time") return validTime(value);
  if (type === "date") return Boolean(calendarDateISO(value));
  const [date, time] = value.split("T");
  return Boolean(calendarDateISO(date) && validTime(time ?? ""));
}

/** DD/MM/YYYY and 24-hour entry, preserving existing ISO onChange contracts. */
export const DateTimeInput = React.forwardRef<HTMLInputElement, Props>(
  ({ type, value, defaultValue, onChange, onBlur, inputClassName, className, onValidityChange, ...props }, forwardedRef) => {
    const source = String(value ?? defaultValue ?? "");
    const [draft, setDraft] = React.useState(() => formatValue(type, source));
    const [touched, setTouched] = React.useState(false);
    const lastEmitted = React.useRef(source);
    const textRef = React.useRef<HTMLInputElement | null>(null);
    const feedbackId = React.useId();
    React.useEffect(() => {
      if (source !== lastEmitted.current) {
        setDraft(formatValue(type, source));
        lastEmitted.current = source;
        setTouched(false);
      }
    }, [source, type]);
    const stored = storedValue(type, draft);
    const valid = isValid(type, stored)
      && (!stored || !props.min || stored >= String(props.min))
      && (!stored || !props.max || stored <= String(props.max));
    const message = type === "time"
      ? "Use 24-hour time HH:mm, from 00:00 to 23:59."
      : `Use a real date as DD/MM/YYYY${type === "datetime-local" ? " and 24-hour time HH:mm" : ""}${props.min || props.max ? " within the allowed range" : ""}.`;
    const invalid = Boolean(draft) && !valid && (touched || draft.replace(/\D/g, "").length >= (type === "time" ? 4 : type === "date" ? 8 : 12));
    React.useEffect(() => { textRef.current?.setCustomValidity(valid ? "" : message); }, [valid, message]);
    React.useEffect(() => { onValidityChange?.(valid && (!props.required || Boolean(draft))); }, [valid, props.required, draft, onValidityChange]);
    const change = (event: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatValue(type, event.target.value);
      const candidate = storedValue(type, formatted);
      const next = isValid(type, candidate)
        && (!candidate || !props.min || candidate >= String(props.min))
        && (!candidate || !props.max || candidate <= String(props.max)) ? candidate : "";
      setDraft(formatted);
      lastEmitted.current = next;
      onChange?.({
        ...event,
        target: { ...event.target, value: next, name: props.name ?? "", id: props.id ?? "", type },
        currentTarget: { ...event.currentTarget, value: next, name: props.name ?? "", id: props.id ?? "", type },
      } as React.ChangeEvent<HTMLInputElement>);
    };
    return (
      <div className="min-w-0 space-y-1">
        <div className="relative min-w-0">
          <input
            {...props}
            type="text"
            inputMode="numeric"
            ref={(node) => {
              textRef.current = node;
              if (typeof forwardedRef === "function") forwardedRef(node);
              else if (forwardedRef) forwardedRef.current = node;
            }}
            value={draft}
            placeholder={type === "time" ? "HH:mm" : type === "date" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"}
            className={cn(inputClassName, className, "pr-11")}
            aria-invalid={props["aria-invalid"] || invalid}
            aria-describedby={[props["aria-describedby"], invalid ? feedbackId : ""].filter(Boolean).join(" ") || undefined}
            onChange={change}
            onBlur={(event) => { setTouched(true); onBlur?.(event); }}
          />
          <span className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground">
            {type === "time" ? <Clock className="h-4 w-4" /> : <CalendarIcon className="h-4 w-4" />}
            <input
              type={type}
              aria-label={`Choose ${type === "time" ? "time" : "date"}${props["aria-label"] ? `: ${props["aria-label"]}` : ""}`}
              value={isValid(type, stored) ? stored : ""}
              min={props.min}
              max={props.max}
              step={props.step ?? (type === "date" ? undefined : 60)}
              disabled={props.disabled || props.readOnly}
              tabIndex={props.disabled || props.readOnly ? -1 : 0}
              onChange={change}
              onClick={(event) => {
                try { event.currentTarget.showPicker?.(); }
                catch { event.currentTarget.focus(); }
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
            />
          </span>
        </div>
        {invalid ? <p id={feedbackId} role="alert" className="text-xs text-destructive">{message}</p> : null}
      </div>
    );
  },
);
DateTimeInput.displayName = "DateTimeInput";
